import React, { useContext, useState } from 'react'
import * as yup from 'yup'
import * as queryString from 'query-string'
import styled from 'styled-components'
import { space, SpaceProps } from 'styled-system'
import { GridItem } from '@zuto/core/src/grid'
import { ArrowRight, Mobile } from '@zuto/icons'
import { Button, Input } from '@zuto/atoms'
import { useCallbackOnce } from '@zuto/utilities'
import Box from '@zuto/core/src/box'
import QuestionComponentProps from '../../../Questions/question-component-props'
import ContactNumberValidator from '../../../validators/ContactNumberValidator'
import QuestionKey from '../../../Questions/question-key'
import { ApplicationFormContext } from '../../ApplicationForm/ApplicationFormContext'
import withQuestion from '../../HOC/withQuestion'
import withFade from '../../HOC/withFade'
import AppFormQuestion from '../../AppFormQuestion'
import { fireReactGAEvent, handlePrevButtonClick } from '../utils'
import NavigationButtons from '../../NavigationButtons/NavigationButtons'

const StyledDiv = styled.div<SpaceProps>`
  ${space}
  `

const StyledContactQuestionHeader = styled(GridItem)`
  border-bottom: 1px solid #D6D6D6;
`

const ContactNumberQuestion = ({
  existingAnswers,
  disablePrevious
}: QuestionComponentProps): JSX.Element => {
  const appFormContext = useContext(ApplicationFormContext)
  const [contactNumber, setContactNumber] = useState(existingAnswers)
  const [inputError, setInputError] = useState<string | undefined>(undefined)
  const [nextButtonCb, resetNextQuestionCb] = useCallbackOnce()
  const [previousButtonCb] = useCallbackOnce()
  const urlParams = queryString.parse(global.location.search)
  const source = urlParams && urlParams.utm_source as string
  const schema = ContactNumberValidator().getValidator()

  const valdiateInput = (): boolean => {
    try {
      schema.validateSync({
        contactNumber
      })
    } catch (e: unknown) {
      const err = e as yup.ValidationError
      fireReactGAEvent({
        category: 'Application Form',
        action: 'Validation Error',
        label: 'Contact Number',
        nonInteraction: true
      })
      setInputError(err.errors[0])
      return false
    }
    setInputError(undefined)
    return true
  }

  const nextQuestion = () => {
    const valid = valdiateInput()
    if (!valid) {
      resetNextQuestionCb()
      return
    }
    appFormContext.dispatch({
      type: 'QUESTION_ANSWERED',
      name: QuestionKey.CONTACT_NUMBER,
      value: contactNumber
    })
  }

  const previousQuestion = () => {
    handlePrevButtonClick(
      appFormContext.dispatch,
      { name: QuestionKey.CONTACT_NUMBER, value: contactNumber }
    )
  }

  const inputOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContactNumber(e.currentTarget.value)
  }

  const handleEnterKeyPress = (e: React.KeyboardEvent) => {
    if (e.code === 'Enter') {
      nextButtonCb(nextQuestion)
    }
  }

  const renderQuestion = (): JSX.Element => {
    if (source?.toLowerCase() === 'clearscore') {
      return (
        <>
          <StyledContactQuestionHeader
            gridColumn={['auto / span 12', '3 / span 8']}
          // gridRow={['auto', '1 / auto']}
          >
            <Box
              textStyle="display.standard.medium"
              mt={5}
              mb={3}
            >
              Getting in touch
            </Box>
            <Box as="p" textStyle="body.current.regular" mb={5}>
              We&apos;ll contact you to discuss your application and
              help you with the next steps.
            </Box>
          </StyledContactQuestionHeader>
          <GridItem
            gridColumn={['auto / span 12', '3 / span 8']}
          // gridRow={['auto', '2 / auto']}
          >
            <AppFormQuestion
              questionKey={QuestionKey.CONTACT_NUMBER}
              mt={6}
              mb={4}
              textStyle="material.body.medium.smallLineHeight"
            >
              What&apos;s your phone number?
            </AppFormQuestion>
            <StyledDiv mb={5}>
              <Input
                label="You will only be contacted by Zuto."
                onChange={inputOnChange}
                onKeyPress={handleEnterKeyPress}
                error={inputError}
                defaultValue={contactNumber}
                name="contact-number"
                icon={<Mobile />}
                type="tel"
                data-testid="contact-number-input"
                maxLength={20}
                data-hj-suppress=""
              />
            </StyledDiv>
          </GridItem>
          <GridItem
            display="flex"
            alignItems="center"
            gridColumn={['1 / span 12', '3 / span 8']}
            // gridRow={['auto', '3 / auto']}
            mb={[4, 0]}
          >
            <Button
              icon={<ArrowRight />}
              onClick={() => nextButtonCb(nextQuestion)}
              disabled={!contactNumber}
              data-testid="continue-button"
              mt={5}
            >
              Continue to the final step
            </Button>
          </GridItem>
        </>
      )
    }

    return (
      <>
        <GridItem
          gridColumn={['auto / span 12', '3 / span 8']}
          minHeight="174px"
        >
          <AppFormQuestion
            questionKey={QuestionKey.CONTACT_NUMBER}
            mt={3}
            mb={2}
            textStyle="material.body.medium.smallLineHeight"
          >
            What&apos;s your telephone number?
          </AppFormQuestion>
          <StyledDiv mb={5}>
            <Input
              label="Please enter your phone number"
              labelVariant="experimental"
              onChange={inputOnChange}
              onKeyPress={handleEnterKeyPress}
              error={inputError}
              defaultValue={contactNumber}
              name="contact-number"
              icon={<Mobile />}
              type="tel"
              data-testid="contact-number-input"
              maxLength={20}
              data-hj-suppress=""
            />
          </StyledDiv>
        </GridItem>
        <NavigationButtons
          nextHandler={() => nextButtonCb(nextQuestion)}
          previousHandler={() => previousButtonCb(previousQuestion)}
          disableNext={!contactNumber}
          disablePrevious={disablePrevious ? 'invisible' : undefined}
        />
      </>
    )
  }

  return (
    <>
      {renderQuestion()}
    </>
  )
}

export default withFade(withQuestion(ContactNumberQuestion))
